import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { getTranslation } from '../data/translations';
import { mockProducts } from '../data/mockData';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowRight, Award, Users, Package, TrendingUp } from 'lucide-react';

const Home = () => {
  const { language } = useLanguage();
  const t = (key) => getTranslation(language, key);

  const stats = [
    {
      icon: Award,
      value: t('home.experienceYears'),
      label: t('home.experienceLabel'),
      color: '#c5a365'
    },
    {
      icon: Users,
      value: t('home.customers'),
      label: t('home.customersLabel'),
      color: '#c5a365'
    },
    {
      icon: Package,
      value: t('home.products'),
      label: t('home.productsLabel'),
      color: '#c5a365'
    },
    {
      icon: TrendingUp,
      value: t('home.teamExperience'),
      label: t('home.teamExperienceLabel'),
      color: '#c5a365'
    }
  ];

  const whyChooseFeatures = [
    {
      title: { en: 'Premium Quality', ar: 'جودة ممتازة' },
      description: { en: 'Sourced from trusted global brands', ar: 'مصدرها من علامات تجارية عالمية موثوقة' },
      icon: '🏆'
    },
    {
      title: { en: 'Reliable Service', ar: 'خدمة موثوقة' },
      description: { en: 'On-time delivery across Oman', ar: 'التسليم في الوقت المحدد في جميع أنحاء عمان' },
      icon: '🚚'
    },
    {
      title: { en: 'Expert Team', ar: 'فريق خبير' },
      description: { en: '15+ years industry experience', ar: 'أكثر من 15 عامًا من الخبرة في الصناعة' },
      icon: '👥'
    },
    {
      title: { en: 'Competitive Pricing', ar: 'أسعار تنافسية' },
      description: { en: 'Best value for your business', ar: 'أفضل قيمة لعملك' },
      icon: '💰'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        className="relative h-[600px] bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(26, 58, 82, 0.85), rgba(26, 58, 82, 0.85)), url('https://images.unsplash.com/photo-1589109807644-924edf14ee09?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxjb21tZXJjaWFsJTIwa2l0Y2hlbnxlbnwwfHx8fDE3NzQyNTQwMzl8MA&ixlib=rb-4.1.0&q=85')`
        }}
      >
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              {t('home.heroTitle')}
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
              {t('home.heroSubtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/products">
                <Button
                  size="lg"
                  className="bg-[#c5a365] hover:bg-[#b39355] text-white px-8 py-6 text-lg font-semibold transition-all duration-300 hover:scale-105 shadow-lg"
                >
                  {t('home.heroCta')}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button
                  size="lg"
                  variant="outline"
                  className="bg-white/10 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-[#1a3a52] px-8 py-6 text-lg font-semibold transition-all duration-300 hover:scale-105"
                >
                  {t('home.heroCtaSecondary')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const IconComponent = stat.icon;
              return (
                <Card
                  key={index}
                  className="text-center border-none shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 bg-gradient-to-br from-white to-gray-50"
                >
                  <CardContent className="p-6">
                    <IconComponent className="h-12 w-12 mx-auto mb-4" style={{ color: stat.color }} />
                    <p className="text-4xl font-bold text-[#1a3a52] mb-2">{stat.value}</p>
                    <p className="text-sm text-gray-600 leading-relaxed">{stat.label}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#1a3a52] mb-4">
              {t('home.whyChooseTitle')}
            </h2>
            <p className="text-xl text-gray-600">{t('home.whyChooseSubtitle')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseFeatures.map((feature, index) => (
              <Card
                key={index}
                className="border-none shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 bg-white"
              >
                <CardContent className="p-8 text-center">
                  <div className="text-5xl mb-4">{feature.icon}</div>
                  <h3 className="text-xl font-bold text-[#1a3a52] mb-3">
                    {feature.title[language]}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {feature.description[language]}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#1a3a52] mb-4">
              {t('home.featuredProductsTitle')}
            </h2>
            <p className="text-xl text-gray-600">{t('home.featuredProductsSubtitle')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {mockProducts.map((product) => (
              <Card
                key={product.id}
                className="overflow-hidden border-none shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
              >
                <div className="aspect-square overflow-hidden bg-gray-100">
                  <img
                    src={product.image}
                    alt={product.name[language]}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <CardContent className="p-6">
                  <p className="text-sm text-[#c5a365] font-semibold mb-2">
                    {product.category[language]}
                  </p>
                  <h3 className="text-lg font-bold text-[#1a3a52] mb-2">
                    {product.name[language]}
                  </h3>
                  <p className="text-sm text-gray-600">{product.description[language]}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center">
            <Link to="/products">
              <Button
                size="lg"
                className="bg-[#1a3a52] hover:bg-[#0f2535] text-white px-8 py-6 text-lg font-semibold transition-all duration-300 hover:scale-105"
              >
                {t('home.viewAllProducts')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#1a3a52] to-[#2a4a62]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {language === 'en' ? 'Ready to Partner with Us?' : 'هل أنت مستعد للشراكة معنا؟'}
          </h2>
          <p className="text-xl text-gray-200 mb-8">
            {language === 'en'
              ? 'Join 200+ satisfied customers across Oman'
              : 'انضم إلى أكثر من 200 عميل راضٍ في جميع أنحاء عمان'}
          </p>
          <Link to="/contact">
            <Button
              size="lg"
              className="bg-[#c5a365] hover:bg-[#b39355] text-white px-8 py-6 text-lg font-semibold transition-all duration-300 hover:scale-105 shadow-lg"
            >
              {t('nav.contact')}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
